import { useEffect, useState } from 'react';
import { fetchSubscriptionLockStatus } from '../services/subscription.service';
import { onAccessTokenChange } from '../utility/tokenManager';
import {
  applySubscriptionLockStatus,
  getSubscriptionLock,
  getSubscriptionLockOwner,
  resetSubscriptionLock,
  subscribeSubscriptionLock,
  SubscriptionLockState,
} from '../utility/subscriptionLock';

/** Upper bound on holding the UI while the lockout answer is unknown. */
const RESOLVE_TIMEOUT_MS = 4000;

/** One request per owner, shared by every mounted consumer (layout + sidebar). */
let inFlight: { owner: string; promise: Promise<void> } | null = null;
/** Only the most recently started request may record its answer. */
let latestRequest = 0;

/**
 * Ask the API whether the signed-in user is locked out, and record the answer.
 * A failed request records "not locked": the API still enforces, and its next
 * 402 locks the screen — the portal must never lock anyone on a network error.
 *
 * `force` starts a new request even if one is already running — used after a
 * payment, when a request sent before it could still answer "locked".
 */
export const refreshSubscriptionLock = (force = false): Promise<void> => {
  const owner = getSubscriptionLockOwner();
  if (!owner) return Promise.resolve();
  if (!force && inFlight?.owner === owner) return inFlight.promise;

  const requestId = ++latestRequest;
  const promise = fetchSubscriptionLockStatus()
    .then((status) => {
      if (requestId === latestRequest) applySubscriptionLockStatus(owner, status);
    })
    .catch(() => {
      if (requestId === latestRequest) applySubscriptionLockStatus(owner, null);
    })
    .finally(() => {
      if (inFlight?.promise === promise) inFlight = null;
    });
  inFlight = { owner, promise };
  return promise;
};

// Sign-in, sign-out and organization switch change the owner: drop the old
// answer and ask again. A token refresh keeps the owner, so it costs nothing.
let lastOwner: string | null = getSubscriptionLockOwner();
onAccessTokenChange(() => {
  const owner = getSubscriptionLockOwner();
  if (owner === lastOwner) return;
  lastOwner = owner;
  resetSubscriptionLock();
  if (owner) void refreshSubscriptionLock();
});

/**
 * Whether this organization is locked out, and why (CVA-4668).
 *
 * `resolved` says whether the answer can be trusted yet. Until it is, callers
 * show a loader rather than the product: a locked-out user must never see the
 * full menu, and the real page must never mount and fire its calls, even
 * briefly. The wait is capped so a slow or failing request cannot hold the
 * portal hostage.
 */
export const useSubscriptionLock = (): SubscriptionLockState => {
  const [state, setState] = useState<SubscriptionLockState>(getSubscriptionLock);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeSubscriptionLock(() => setState(getSubscriptionLock()));
    // Pick up anything that changed between render and subscribe, then ask
    // again: the same user signing back in may have paid in the meantime. A
    // known answer stays on screen while the request runs, so nothing flashes.
    setState(getSubscriptionLock());
    void refreshSubscriptionLock();

    // A lockout can start or end while the tab is in the background.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') void refreshSubscriptionLock();
    };
    window.addEventListener('visibilitychange', onVisibility);

    return () => {
      unsubscribe();
      window.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Restart the wait for every new owner, so switching organization shows the
  // loader again instead of the new organization's product.
  useEffect(() => {
    setTimedOut(false);
    if (state.resolved) return undefined;
    const timer = setTimeout(() => setTimedOut(true), RESOLVE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [state.owner, state.resolved]);

  return state.resolved || !timedOut ? state : { ...state, resolved: true };
};

export default useSubscriptionLock;
